<script setup lang="ts">
const props = withDefaults(defineProps<{
  modelValue?: boolean
  maxWidth?: string | number
  persistent?: boolean
  fullscreen?: boolean
  scrollable?: boolean
}>(), {
  maxWidth: 600,
})

const emit = defineEmits<{
  'update:modelValue': [val: boolean]
}>()

function close() {
  if (!props.persistent) {
    emit('update:modelValue', false)
  }
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') close()
}
</script>

<template>
  <Teleport to="body">
    <Transition name="m-dialog">
      <div v-if="modelValue" class="m-dialog__overlay" @click.self="close" @keydown="onKeydown">
        <div
          class="m-dialog__content"
          :class="{ 'm-dialog--fullscreen': fullscreen, 'm-dialog--scrollable': scrollable }"
          :style="{ maxWidth: fullscreen ? '100%' : (typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth) }"
          role="dialog"
          aria-modal="true"
        >
          <slot />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.m-dialog__overlay {
  position: fixed;
  inset: 0;
  z-index: var(--m-z-modal);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  padding: var(--m-space-4);
}

.m-dialog__content {
  width: 100%;
  background: var(--m-surface);
  border-radius: var(--m-radius-md);
  box-shadow: var(--m-shadow-xl);
  overflow: hidden;
  max-height: 90vh;
}

.m-dialog--scrollable { overflow-y: auto; }

.m-dialog--fullscreen {
  max-width: 100% !important;
  max-height: 100% !important;
  height: 100%;
  border-radius: 0;
}

/* Transitions */
.m-dialog-enter-active,
.m-dialog-leave-active {
  transition: opacity var(--m-transition-slow);
}
.m-dialog-enter-active .m-dialog__content,
.m-dialog-leave-active .m-dialog__content {
  transition: transform var(--m-transition-slow), opacity var(--m-transition-slow);
}

.m-dialog-enter-from,
.m-dialog-leave-to { opacity: 0; }

.m-dialog-enter-from .m-dialog__content { transform: scale(0.95); opacity: 0; }
.m-dialog-leave-to .m-dialog__content { transform: scale(0.95); opacity: 0; }
</style>
